import React, { useEffect, useState } from "react";
import { apiFetch, abrirArchivoProtegido } from "../utils/api";
import { Ayuda } from "../context/AyudaContext";
import Cargando from "../components/Cargando";

const TIPOS = [
  { valor: "", texto: "Todos", icono: "🗂️" },
  { valor: "entrega", texto: "Acta de Entrega", icono: "📋" },
  { valor: "bienes", texto: "Acta de Bienes", icono: "🏷️" },
];

const INFO_TIPO = {
  entrega: { texto: "Entrega", color: "var(--tema-primario, #2f6690)", bg: "var(--tema-suave, #dceaf5)" },
  bienes: { texto: "Bienes", color: "#3d5a99", bg: "#e1e7f5" },
};

const TAMANOS_PAGINA = ["10", "20", "50", "todas"];

function Actas() {
  const [actas, setActas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const [tipo, setTipo] = useState("");
  const [persona, setPersona] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const [tamanoPagina, setTamanoPagina] = useState("10");
  const [pagina, setPagina] = useState(1);

  const cargar = () => {
    setCargando(true);
    setError("");
    const params = new URLSearchParams();
    if (tipo) params.set("tipo", tipo);
    if (persona) params.set("persona", persona);
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);

    apiFetch(`/api/actas?${params.toString()}`)
      .then((data) => setActas(data || []))
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  };

  // El tipo filtra desde el servidor y se re-consulta apenas se toca la
  // pestaña, sin esperar al botón "Buscar" (que sí es necesario para
  // persona/fecha, para no golpear el backend en cada tecla).
  useEffect(cargar, [tipo]);

  const buscar = (e) => {
    e.preventDefault();
    setPagina(1);
    cargar();
  };

  const cambiarTipo = (valor) => {
    setTipo(valor);
    setPagina(1);
  };

  const verPdf = async (acta) => {
    setError("");
    try {
      const ruta =
        acta.tipo === "entrega"
          ? `/api/solicitudes/${acta.solicitud_id}/acta?acta_id=${acta.id}`
          : `/api/solicitudes/${acta.solicitud_id}/acta-bienes?acta_id=${acta.id}`;
      await abrirArchivoProtegido(ruta);
    } catch (err) {
      setError(err.message);
    }
  };

  // "persona" filtra al vuelo sobre lo ya cargado (sin pegarle al backend en
  // cada tecla) — el botón "Buscar" sigue sirviendo para combinarlo con las
  // fechas, que sí necesitan re-consultar el servidor.
  const terminoPersona = persona.trim().toLowerCase();
  const actasFiltradas = actas.filter(
    (a) => !terminoPersona || a.recibido_por?.toLowerCase().includes(terminoPersona)
  );

  const totalPaginas =
    tamanoPagina === "todas" ? 1 : Math.max(1, Math.ceil(actasFiltradas.length / Number(tamanoPagina)));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visibles =
    tamanoPagina === "todas"
      ? actasFiltradas
      : actasFiltradas.slice((paginaSegura - 1) * Number(tamanoPagina), paginaSegura * Number(tamanoPagina));

  return (
    <div>
      <h2 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)", textAlign: "center" }}>🧾 Histórico de Actas</h2>
      <Ayuda>
        <p style={{ color: "#5b6472", marginTop: "0.25rem", textAlign: "center" }}>
          Todas las actas generadas al registrar una entrega — cada una con su propio número,
          distinto del folio del pedido. Se pueden reimprimir en cualquier momento.
        </p>
      </Ayuda>

      <div style={styles.card}>
        <div style={styles.pills}>
          {TIPOS.map((t) => (
            <button
              key={t.valor}
              type="button"
              onClick={() => cambiarTipo(t.valor)}
              style={{ ...styles.pill, ...(tipo === t.valor ? styles.pillActivo : {}) }}
            >
              {t.icono} {t.texto}
            </button>
          ))}
        </div>

        <form onSubmit={buscar} className="barra-filtros" style={styles.filtros}>
          <div style={{ position: "relative" }}>
            <span style={styles.iconoBusqueda}>🔍</span>
            <input
              type="text"
              placeholder="Buscar por quién recibió..."
              value={persona}
              onChange={(e) => {
                setPersona(e.target.value);
                setPagina(1);
              }}
              style={{ ...styles.input, paddingLeft: "1.9rem" }}
            />
          </div>
          <label style={styles.campoFecha}>
            Desde
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={styles.input} />
          </label>
          <label style={styles.campoFecha}>
            Hasta
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={styles.input} />
          </label>
          <button type="submit" style={styles.botonBuscar}>
            🔍 Buscar
          </button>
        </form>

        {error && <p style={{ color: "#a3402f" }}>{error}</p>}

        {cargando ? (
          <Cargando compacto />
        ) : actas.length === 0 ? (
          <p style={{ marginTop: "0.75rem", color: "#5b6472" }}>📭 No hay actas registradas con estos filtros.</p>
        ) : actasFiltradas.length === 0 ? (
          <p style={{ marginTop: "0.75rem", color: "#5b6472" }}>
            📭 Ninguna acta coincide con "{persona}".
          </p>
        ) : (
          <>
            <div className="table-scroll" style={styles.tablaWrap}>
              <table style={styles.tabla}>
                <thead>
                  <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                    <th style={styles.th}>N.° Acta</th>
                    <th style={styles.th}>Tipo</th>
                    <th style={styles.th}>Pedido</th>
                    <th style={styles.th}>Entregado a</th>
                    <th style={styles.th}>Artículos</th>
                    <th style={styles.th}>Fecha</th>
                    <th style={styles.th}>Registró</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((a, i) => {
                    const info = INFO_TIPO[a.tipo] || INFO_TIPO.entrega;
                    return (
                      <tr
                        key={a.id}
                        style={{ borderBottom: "1px solid #eef1f3", background: i % 2 === 1 ? "#fafbfc" : "transparent" }}
                      >
                        <td style={{ ...styles.td, fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" }}>
                          ACTA-{String(a.numero_acta).padStart(4, "0")}
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, color: info.color, background: info.bg }}>{info.texto}</span>
                        </td>
                        <td style={styles.td}>#{String(a.solicitud_id).padStart(3, "0")}</td>
                        <td style={styles.td}>{a.recibido_por || "—"}</td>
                        <td style={{ ...styles.td, maxWidth: 320 }}>{a.items_texto || "—"}</td>
                        <td style={styles.td}>{new Date(a.creado_en).toLocaleString("es-NI")}</td>
                        <td style={styles.td}>{a.creado_por_nombre}</td>
                        <td style={styles.td}>
                          <button type="button" onClick={() => verPdf(a)} style={styles.botonVer}>
                            📄 Ver / Reimprimir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={styles.pieTabla}>
              <span style={{ fontSize: "0.8rem", color: "#5b6472" }}>
                Mostrando {(paginaSegura - 1) * (tamanoPagina === "todas" ? actasFiltradas.length : Number(tamanoPagina)) + 1}
                {" a "}
                {Math.min(
                  paginaSegura * (tamanoPagina === "todas" ? actasFiltradas.length : Number(tamanoPagina)),
                  actasFiltradas.length
                )}
                {" de "}
                {actasFiltradas.length}
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                {totalPaginas > 1 && (
                  <div style={{ display: "flex", gap: "0.3rem" }}>
                    <button
                      type="button"
                      disabled={paginaSegura === 1}
                      onClick={() => setPagina(paginaSegura - 1)}
                      style={styles.botonPagina}
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPaginas }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPagina(i + 1)}
                        style={{ ...styles.botonPagina, ...(paginaSegura === i + 1 ? styles.botonPaginaActivo : {}) }}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={paginaSegura === totalPaginas}
                      onClick={() => setPagina(paginaSegura + 1)}
                      style={styles.botonPagina}
                    >
                      ›
                    </button>
                  </div>
                )}
                <label style={{ fontSize: "0.8rem", color: "#5b6472", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  Por página:
                  <select
                    value={tamanoPagina}
                    onChange={(e) => {
                      setTamanoPagina(e.target.value);
                      setPagina(1);
                    }}
                    style={{ ...styles.input, padding: "0.3rem 0.4rem" }}
                  >
                    {TAMANOS_PAGINA.map((t) => (
                      <option key={t} value={t}>
                        {t === "todas" ? "Todas" : t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.25rem 1.4rem",
    marginTop: "1.1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  pills: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
    marginBottom: "1.1rem",
  },
  pill: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "#5b6472",
    borderRadius: 999,
    padding: "0.4rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  pillActivo: {
    background: "var(--tema-titulo, #0b3c60)",
    borderColor: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
  },
  filtros: {
    display: "flex",
    gap: "0.6rem",
    flexWrap: "wrap",
    alignItems: "end",
    marginBottom: "1.25rem",
    paddingBottom: "1.1rem",
    borderBottom: "1px solid #eef1f3",
  },
  input: {
    padding: "0.5rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    fontSize: "0.85rem",
  },
  iconoBusqueda: {
    position: "absolute",
    left: "0.55rem",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.8rem",
    pointerEvents: "none",
  },
  campoFecha: { display: "flex", flexDirection: "column", gap: "0.2rem", fontSize: "0.8rem" },
  botonBuscar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  tablaWrap: { border: "1px solid #eef1f3", borderRadius: 10 },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" },
  th: {
    padding: "0.55rem 0.5rem",
    background: "#eef1f3",
    border: "1px solid #d3d9df",
    fontSize: "0.76rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  td: { padding: "0.5rem 0.6rem", textAlign: "left" },
  badge: {
    display: "inline-block",
    padding: "0.15rem 0.55rem",
    borderRadius: 999,
    fontSize: "0.75rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  botonVer: {
    border: "1px solid var(--tema-primario, #2f6690)",
    background: "var(--tema-suave, #eef4fb)",
    borderRadius: 8,
    padding: "0.35rem 0.7rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "var(--tema-primario, #2f6690)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  pieTabla: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.75rem",
    marginTop: "0.9rem",
  },
  botonPagina: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    minWidth: 30,
  },
  botonPaginaActivo: {
    background: "var(--tema-titulo, #0b3c60)",
    borderColor: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
  },
};

export default Actas;
